import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Avatar,
  Grid,
  Divider,
  CircularProgress,
  Tabs,
  Tab,
  TextField,
  Modal,
  IconButton,
  Snackbar,
  Alert,
  Dialog,
  DialogContent,
  Stack,
  Link,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { useDropzone } from "react-dropzone";
import api, { API_BASE_URL } from "../../utils/api";

// Icons
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoIcon from "@mui/icons-material/Info";
import CategoryIcon from "@mui/icons-material/Category";
import HomeIcon from "@mui/icons-material/Home";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CommentIcon from "@mui/icons-material/Comment";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import CloseIcon from "@mui/icons-material/Close";
import PhoneIcon from "@mui/icons-material/Phone";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import EmailIcon from "@mui/icons-material/Email";
import AddCommentIcon from "@mui/icons-material/AddComment";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import WcIcon from "@mui/icons-material/Wc";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import PersonIcon from "@mui/icons-material/Person";

// Components
import ViolationsTimeline from "../OwnerDashboard/Actions/ViolationsTimeline";
import Visits from "../Visits";
import * as MuiIcons from "@mui/icons-material";

const MemberViolationDetails = ({ id: propId, onClose } = {}) => {
  const { id: paramId } = useParams();
  const id = propId ?? paramId;
  const navigate = useNavigate();
  const theme = useTheme();

  const [violationDetails, setViolationDetails] = useState(null);
  const [violationType, setViolationType] = useState(null);
  const [member, setMember] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [filePreview, setFilePreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [files, setFiles] = useState([]);
  const [previewModal, setPreviewModal] = useState({ open: false, src: "", isPdf: false, blobUrl: null, loading: false });

  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [imageURL, setImageURL] = useState(null);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [openCommentImageModal, setOpenCommentImageModal] = useState(false);
  const [commentImageUrl, setCommentImageUrl] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const storedUser = sessionStorage.getItem("user");
  let currentUserId = null;
  let currentUserName = "Unknown";
  let currentUserLastname = "";

  if (storedUser) {
    try {
      const parsed = JSON.parse(storedUser);
      currentUserId = parsed?.userId || null;
      currentUserName = parsed?.firstName || "Unknown";
      currentUserLastname = parsed?.lastName || "";
    } catch (err) {
      console.error("❌ Failed to parse user:", err);
    }
  }

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const softCardSx = {
    borderRadius: "16px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
    backgroundColor: "#fff",
  };

  const softInsetSx = {
    borderRadius: "14px",
    border: "1px solid rgba(47,99,173,0.08)",
    backgroundColor: "rgba(47,99,173,0.03)",
  };

  const sectionIconWrapSx = {
    width: 36,
    height: 36,
    minWidth: 36,
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(47,99,173,0.08)",
    flexShrink: 0,
  };

  const infoRowSx = {
    display: "flex",
    alignItems: "flex-start",
    gap: 1,
    py: 0.75,
    minWidth: 0,
    height: "100%",
  };

  const iconWrapSx = {
    width: 34,
    height: 34,
    minWidth: 34,
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(47,99,173,0.08)",
    flexShrink: 0,
  };

  const labelSx = {
    color: "#8a94a6",
    display: "block",
    lineHeight: 1.2,
    mb: 0.25,
  };

  const valueSx = {
    color: "#223142",
    fontWeight: 600,
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    lineHeight: 1.3,
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const dialogPaperSx = {
    borderRadius: "18px",
    background: "rgba(255,255,255,0.98)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
  };

  const SectionTitle = ({ icon, title, subtitle, action }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        flexWrap: "wrap",
        mb: 1.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
        <Box sx={sectionIconWrapSx}>{icon}</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: "#223142", lineHeight: 1.2 }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.3 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      {action}
    </Box>
  );

  const DetailCard = ({ icon, label, value, chip = null }) => (
    <Card sx={{ ...softCardSx, p: 2, height: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
        <Box sx={sectionIconWrapSx}>{icon}</Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="caption"
            sx={{ color: "#8a94a6", display: "block", mb: 0.3 }}
          >
            {label}
          </Typography>
          {chip ? (
            chip
          ) : (
            <Typography
              variant="body2"
              sx={{
                color: "#223142",
                fontWeight: 600,
                wordBreak: "break-word",
                overflowWrap: "anywhere",
              }}
            >
              {value || "N/A"}
            </Typography>
          )}
        </Box>
      </Box>
    </Card>
  );

  const { getRootProps, getInputProps } = useDropzone({
    accept: "image/*",
    multiple: false,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        setSelectedFile(file);
        setFilePreview(URL.createObjectURL(file));
      }
    },
  });

  const getFormattedDate = (dateString) => {
    if (!dateString) return "N/A";
    const parsed = new Date(dateString);
    if (Number.isNaN(parsed.getTime())) return "N/A";

    return parsed.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const { data: violationData } = await api.get(`/getViolation/${id}`);
        setViolationDetails(violationData);

        if (violationData.property_id) {
          const memberResp = await api.get(`/members/${violationData.property_id}`);
          setMember(memberResp.data);
        }

        if (violationData.ccr_violation_type_id) {
          const { data: types } = await api.get(`/getCcrViolationTypes`);
          const type = types.find((t) => t.id === violationData.ccr_violation_type_id);
          setViolationType(type || null);
        }

        const statusResp = await api.get(`/statuses`);
        setStatuses(statusResp.data);

        const commentsResp = await api.get(`/getExternalViolationComments/${id}`);
        const list = Array.isArray(commentsResp.data) ? commentsResp.data : [];
        setComments(
          list.map((c) => ({
            ...c,
            attachments: Array.isArray(c.attachments) ? c.attachments : [],
          }))
        );

        if (violationData.upload_urls) {
          const sanitizedFilename = encodeURIComponent(violationData.upload_urls.trim());
          setImageURL(`${API_BASE_URL}/violationsimages/${violationData.id}/${sanitizedFilename}`);
        }
      } catch (err) {
        console.error("Failed to fetch violation details:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetails();
  }, [id]);

  useEffect(() => {
    if (!violationDetails?.id) return;

    const fetchFiles = async () => {
      try {
        const res = await api.get(`/violation-files/${violationDetails.id}`);
        setFiles(res.data || []);
      } catch (err) {
        console.error("❌ Failed to load files", err);
      }
    };

    fetchFiles();
  }, [violationDetails?.id]);

  // Opens a file in the preview modal. For PDFs: fetches as blob first so the browser
  // ignores any Content-Disposition: attachment header from the server and renders inline.
  const openPreview = useCallback(async (src, isPdf) => {
    if (!isPdf) {
      setPreviewModal({ open: true, src, isPdf: false, blobUrl: null, loading: false });
      return;
    }
    setPreviewModal({ open: true, src, isPdf: true, blobUrl: null, loading: true });
    try {
      const res = await fetch(src, { credentials: "include" });
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPreviewModal((prev) => ({ ...prev, blobUrl, loading: false }));
    } catch (err) {
      console.error("PDF blob fetch failed, falling back to direct URL", err);
      setPreviewModal((prev) => ({ ...prev, blobUrl: null, loading: false }));
    }
  }, []);

  const closePreview = useCallback(() => {
    setPreviewModal((prev) => {
      if (prev.blobUrl) URL.revokeObjectURL(prev.blobUrl);
      return { open: false, src: "", isPdf: false, blobUrl: null, loading: false };
    });
  }, []);

  const handleTabChange = (e, val) => setActiveTab(val);

  const calculateDaysOpen = (createdAt) => {
    if (!createdAt) return "N/A";
    const createdDate = new Date(createdAt);
    const today = new Date();
    const diffTime = Math.abs(today - createdDate);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getStatusInfo = (statusName) =>
    statuses.find((s) => s.statusname === statusName);

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      return alert("Comment cannot be empty.");
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("violation_id", id);
      formData.append("user_id", currentUserId);
      formData.append("comment_text", newComment);
      formData.append("is_internal", 0);

      if (selectedFile) {
        formData.append("upload_urls", selectedFile);
      }

      const response = await api.post(`/addViolationComment`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setComments((prev) => [
        {
          comment_id: response.data.comment_id,
          comment_text: newComment,
          sent_at: response.data.sent_at || new Date().toISOString(),
          user_name: currentUserName,
          user_lastname: currentUserLastname,
          upload_urls: response.data.upload_urls || null,
          attachments: [],
        },
        ...prev,
      ]);

      setNewComment("");
      setSelectedFile(null);
      setFilePreview(null);
      setIsCommentModalOpen(false);
      setShowConfirm(true);
    } catch (err) {
      console.error("❌ Failed to add comment:", err);
      alert("Failed to add comment. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: "50vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!violationDetails) {
    return (
      <Typography color="error" sx={{ textAlign: "center", mt: 4 }}>
        Failed to load violation details.
      </Typography>
    );
  }

  const memberPhotoUrl = `${API_BASE_URL}/images/${encodeURIComponent(
    member?.PhotoGraphicFileName?.trim() || ""
  )}`;

  const address = member?.FullAddress?.trim() || "N/A";
  const mapsLink =
    address !== "N/A"
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
      : null;

  const phone = member?.PhoneNumber?.trim() || null;
  const altPhone = member?.AlternatePhoneNumber?.trim() || null;
  const cellPhone = member?.CellPhoneNumber?.trim() || null;
  const email = member?.EmailAddress?.trim() || null;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        onClick={() => onClose ? onClose() : navigate("/home/memberspage")}
        sx={{ ...secondaryButtonSx, mb: 3 }}
      >
        Back
      </Button>

      <Grid container spacing={3}>
        {member && (
          <Grid item xs={12} md={4}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Card sx={{ ...pageCardSx, overflow: "hidden" }}>
                <Box
                  sx={{
                    px: 2.5,
                    pt: 2.5,
                    pb: 2,
                    borderBottom: "1px solid rgba(47,99,173,0.08)",
                    backgroundColor: "rgba(47,99,173,0.02)",
                    textAlign: "center",
                  }}
                >
                  <Avatar
                    src={memberPhotoUrl}
                    alt={member?.FirstName}
                    sx={{
                      width: 112,
                      height: 112,
                      margin: "0 auto",
                      mb: 1.75,
                      border: "4px solid rgba(47,99,173,0.16)",
                      boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: "#223142", lineHeight: 1.2 }}
                  >
                    {`${member?.FirstName || ""} ${member?.LastName || ""}`.trim() ||
                      "Member"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.5 }}>
                    Member Code: {member?.ClubMemberCode || "N/A"}
                  </Typography>

                  <Box sx={{ mt: 1.5 }}>
                    <Chip
                      label={member?.ClubStatusRuleCode || "Unknown"}
                      color="success"
                      size="small"
                      icon={<VerifiedUserIcon sx={{ color: "white !important" }} />}
                      sx={{
                        fontWeight: 700,
                        height: 30,
                        borderRadius: "999px",
                      }}
                    />
                  </Box>
                </Box>

                <Box sx={{ p: 2.5 }}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={infoRowSx}>
                        <Box sx={iconWrapSx}>
                          <PersonIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="caption" sx={labelSx}>
                            Member
                          </Typography>
                          <Typography variant="body2" sx={valueSx}>
                            {member?.ClubMemberCode || "N/A"}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={infoRowSx}>
                        <Box sx={iconWrapSx}>
                          <WcIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="caption" sx={labelSx}>
                            Gender
                          </Typography>
                          <Typography variant="body2" sx={valueSx}>
                            {member?.Gender || "N/A"}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={infoRowSx}>
                        <Box sx={iconWrapSx}>
                          <CalendarTodayIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="caption" sx={labelSx}>
                            Birth Date
                          </Typography>
                          <Typography variant="body2" sx={valueSx}>
                            {getFormattedDate(member?.BirthDate)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={infoRowSx}>
                        <Box sx={iconWrapSx}>
                          <CalendarTodayIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="caption" sx={labelSx}>
                            Join Date
                          </Typography>
                          <Typography variant="body2" sx={valueSx}>
                            {getFormattedDate(member?.JoinDate)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={infoRowSx}>
                        <Box sx={iconWrapSx}>
                          <PhoneIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="caption" sx={labelSx}>
                            Phone
                          </Typography>
                          {phone ? (
                            <Link
                              href={`tel:${phone}`}
                              underline="hover"
                              sx={{
                                color: "#2f63ad",
                                fontSize: "0.95rem",
                                fontWeight: 600,
                                wordBreak: "break-word",
                              }}
                            >
                              {phone}
                            </Link>
                          ) : (
                            <Typography variant="body2" sx={valueSx}>
                              N/A
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={infoRowSx}>
                        <Box sx={iconWrapSx}>
                          <ContactPhoneIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="caption" sx={labelSx}>
                            Alt Phone
                          </Typography>
                          {altPhone ? (
                            <Link
                              href={`tel:${altPhone}`}
                              underline="hover"
                              sx={{
                                color: "#2f63ad",
                                fontSize: "0.95rem",
                                fontWeight: 600,
                                wordBreak: "break-word",
                              }}
                            >
                              {altPhone}
                            </Link>
                          ) : (
                            <Typography variant="body2" sx={valueSx}>
                              N/A
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={infoRowSx}>
                        <Box sx={iconWrapSx}>
                          <SmartphoneIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="caption" sx={labelSx}>
                            Cell
                          </Typography>
                          {cellPhone ? (
                            <Link
                              href={`tel:${cellPhone}`}
                              underline="hover"
                              sx={{
                                color: "#2f63ad",
                                fontSize: "0.95rem",
                                fontWeight: 600,
                                wordBreak: "break-word",
                              }}
                            >
                              {cellPhone}
                            </Link>
                          ) : (
                            <Typography variant="body2" sx={valueSx}>
                              N/A
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={infoRowSx}>
                        <Box sx={iconWrapSx}>
                          <EmailIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="caption" sx={labelSx}>
                            Email
                          </Typography>
                          {email ? (
                            <Link
                              href={`mailto:${email}`}
                              underline="hover"
                              sx={{
                                color: "#2f63ad",
                                fontSize: "0.95rem",
                                fontWeight: 600,
                                wordBreak: "break-word",
                                overflowWrap: "anywhere",
                              }}
                            >
                              {email}
                            </Link>
                          ) : (
                            <Typography variant="body2" sx={valueSx}>
                              N/A
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={infoRowSx}>
                        <Box sx={iconWrapSx}>
                          <HomeIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="caption" sx={labelSx}>
                            Address
                          </Typography>
                          {mapsLink ? (
                            <Link
                              href={mapsLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              sx={{
                                color: "#2f63ad",
                                fontSize: "0.95rem",
                                fontWeight: 600,
                                wordBreak: "break-word",
                                overflowWrap: "anywhere",
                              }}
                            >
                              {address}
                            </Link>
                          ) : (
                            <Typography variant="body2" sx={valueSx}>
                              N/A
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Card>

              <Card sx={{ ...pageCardSx, overflow: "hidden" }}>
                <Box
                  sx={{
                    px: 2.5,
                    pt: 2.25,
                    pb: 1.5,
                    borderBottom: "1px solid rgba(47,99,173,0.08)",
                    backgroundColor: "rgba(47,99,173,0.02)",
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
                    Activity Timeline
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.4 }}>
                    Violation activity, comments, visits, and updates.
                  </Typography>
                </Box>

                <Box sx={{ p: 2.5 }}>
                  <Box
                    sx={{
                      borderRadius: "14px",
                      border: "1px solid rgba(47,99,173,0.08)",
                      backgroundColor: "rgba(47,99,173,0.02)",
                      p: 1.5,
                    }}
                  >
                    <ViolationsTimeline violation_id={id} />
                  </Box>
                </Box>
              </Card>
            </Box>
          </Grid>
        )}

        <Grid item xs={12} md={8}>
          <Card sx={{ ...pageCardSx, overflow: "hidden" }}>
            <Box
              sx={{
                px: { xs: 2, sm: 2.5, md: 3 },
                pt: 2.5,
                pb: 2,
                borderBottom: "1px solid rgba(47,99,173,0.08)",
                backgroundColor: "rgba(47,99,173,0.02)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: { xs: "flex-start", sm: "center" },
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1.25,
                }}
              >
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142" }}>
                    Violation Details
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.5 }}>
                    Review violation details, comments, files, and visits.
                  </Typography>
                </Box>

                <Chip
                  label={`Open ${calculateDaysOpen(violationDetails.created_at)} days`}
                  sx={{
                    borderRadius: "999px",
                    fontWeight: 700,
                    backgroundColor: "rgba(47,99,173,0.10)",
                    color: "#2f63ad",
                  }}
                />
              </Box>

              <Typography variant="body2" sx={{ color: "#6b778c", mt: 1.5 }}>
                Violation ID: <strong>VLT-{violationDetails.id}</strong>
              </Typography>
            </Box>

            <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="fullWidth"
                sx={{
                  mb: 3,
                  minHeight: 44,
                  backgroundColor: "rgba(47,99,173,0.03)",
                  borderRadius: "12px",
                  p: 0.5,
                  "& .MuiTabs-indicator": { display: "none" },
                  "& .MuiTab-root": {
                    minHeight: 40,
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: "10px",
                    color: "#6b778c",
                  },
                  "& .Mui-selected": {
                    backgroundColor: "#fff",
                    color: "#223142 !important",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  },
                }}
              >
                <Tab label="Details" />
                <Tab label="Comments" />
                <Tab label="Visits" />
              </Tabs>

              {activeTab === 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {imageURL && (
                    <Card sx={{ ...softCardSx, p: 2 }}>
                      <SectionTitle
                        icon={<ImageOutlinedIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
                        title="Violation Image"
                        subtitle="Click the image to preview it larger."
                      />

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          cursor: "pointer",
                        }}
                        onClick={() => setOpenImageModal(true)}
                      >
                        <Box
                          component="img"
                          src={imageURL}
                          alt="Violation"
                          sx={{
                            width: "100%",
                            maxWidth: 240,
                            borderRadius: "12px",
                            border: "2px solid rgba(47,99,173,0.10)",
                            boxShadow: "0 6px 12px rgba(0,0,0,0.08)",
                          }}
                        />
                      </Box>
                    </Card>
                  )}

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <DetailCard
                        icon={<InfoIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
                        label="Violation ID"
                        value={`VLT-${violationDetails.id}`}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <DetailCard
                        icon={<CategoryIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
                        label="Type"
                        value={violationType?.title || "N/A"}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <DetailCard
                        icon={<HomeIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
                        label="Property"
                        value={violationDetails.property_id || "N/A"}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <DetailCard
                        icon={<CalendarTodayIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
                        label="Violated At"
                        value={
                          violationDetails.violated_at
                            ? new Date(violationDetails.violated_at).toLocaleString()
                            : "N/A"
                        }
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <DetailCard
                        icon={<ReportProblemIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
                        label="Created"
                        value={
                          violationDetails.created_at
                            ? new Date(violationDetails.created_at).toLocaleString()
                            : "N/A"
                        }
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Card sx={{ ...softCardSx, p: 2, height: "100%" }}>
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
                          <Box sx={sectionIconWrapSx}>
                            {(() => {
                              const statusInfo = getStatusInfo(violationDetails.status);
                              const IconComponent =
                                statusInfo?.icon && MuiIcons[statusInfo.icon];
                              const color =
                                theme.palette[statusInfo?.color]?.main ||
                                statusInfo?.color;

                              return IconComponent ? (
                                <IconComponent fontSize="small" sx={{ color }} />
                              ) : (
                                <MuiIcons.InfoOutlined
                                  fontSize="small"
                                  sx={{ color: "#2f63ad" }}
                                />
                              );
                            })()}
                          </Box>

                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              variant="caption"
                              sx={{ color: "#8a94a6", display: "block", mb: 0.3 }}
                            >
                              Status
                            </Typography>

                            {(() => {
                              const statusInfo = getStatusInfo(violationDetails.status);
                              const color =
                                theme.palette[statusInfo?.color]?.main ||
                                statusInfo?.color ||
                                "#64748b";

                              return (
                                <Chip
                                  label={statusInfo?.statusname || "Unknown"}
                                  size="small"
                                  sx={{
                                    borderRadius: "999px",
                                    backgroundColor: color,
                                    color: "#fff",
                                    fontWeight: 700,
                                  }}
                                />
                              );
                            })()}
                          </Box>
                        </Box>
                      </Card>
                    </Grid>

                    <Grid item xs={12}>
                      <DetailCard
                        icon={<CommentIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
                        label="Comment"
                        value={violationDetails.comment || "N/A"}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Card sx={{ ...softCardSx, p: 2 }}>
                        <SectionTitle
                          icon={<FolderOutlinedIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
                          title="Files"
                          subtitle="Uploaded files for this violation."
                        />

                        {files.length === 0 ? (
                          <Typography variant="body2" sx={{ color: "#6b778c" }}>
                            No files uploaded.
                          </Typography>
                        ) : (
                          <Grid container spacing={2}>
                            {files.map((file) => {
                              const ext = (file.file_name || "").split(".").pop()?.toLowerCase() || "";
                              const fileType = file.file_type || "";
                              const isImage =
                                fileType.startsWith("image/") ||
                                ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(ext);
                              const isPDF =
                                fileType.includes("pdf") || ext === "pdf";

                              const fileUrl = `${API_BASE_URL}/violationsimages/${violationDetails.id}/${encodeURIComponent(file.file_name)}`;

                              return (
                                <Grid item xs={12} sm={6} md={4} key={file.id}>
                                  <Card
                                    sx={{
                                      ...softInsetSx,
                                      p: 2,
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      gap: 1,
                                      textAlign: "center",
                                      height: "100%",
                                      backgroundColor: "#fff",
                                    }}
                                  >
                                    {isImage ? (
                                      <>
                                        <Box
                                          component="img"
                                          src={fileUrl}
                                          alt={file.file_name}
                                          sx={{
                                            maxWidth: "100%",
                                            maxHeight: 120,
                                            borderRadius: "8px",
                                            cursor: "pointer",
                                            objectFit: "cover",
                                          }}
                                          onClick={() => openPreview(fileUrl, false)}
                                        />
                                        <Typography variant="body2" sx={{ mt: 0.5, wordBreak: "break-word" }}>
                                          {file.file_name}
                                        </Typography>
                                        <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<VisibilityIcon />}
                                            onClick={() => openPreview(fileUrl, false)}
                                            sx={secondaryButtonSx}
                                          >
                                            Preview
                                          </Button>
                                          <Button
                                            size="small"
                                            variant="contained"
                                            href={fileUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            download
                                            sx={primaryButtonSx}
                                          >
                                            Download
                                          </Button>
                                        </Box>
                                      </>
                                    ) : isPDF ? (
                                      <>
                                        <Box
                                          sx={{
                                            minHeight: 120,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            width: "100%",
                                          }}
                                        >
                                          <MuiIcons.PictureAsPdf color="error" sx={{ fontSize: 52 }} />
                                        </Box>
                                        <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                                          {file.file_name}
                                        </Typography>
                                        <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<VisibilityIcon />}
                                            onClick={() => openPreview(fileUrl, true)}
                                            sx={secondaryButtonSx}
                                          >
                                            Preview
                                          </Button>
                                          <Button
                                            size="small"
                                            variant="contained"
                                            href={fileUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            download
                                            sx={primaryButtonSx}
                                          >
                                            Download
                                          </Button>
                                        </Box>
                                      </>
                                    ) : (
                                      <>
                                        <Box
                                          sx={{
                                            minHeight: 120,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            width: "100%",
                                          }}
                                        >
                                          <FolderOutlinedIcon sx={{ fontSize: 52, color: "#2f63ad" }} />
                                        </Box>
                                        <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                                          {file.file_name}
                                        </Typography>
                                        <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            href={fileUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={secondaryButtonSx}
                                          >
                                            Open
                                          </Button>
                                          <Button
                                            size="small"
                                            variant="contained"
                                            href={fileUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            download
                                            sx={primaryButtonSx}
                                          >
                                            Download
                                          </Button>
                                        </Box>
                                      </>
                                    )}
                                  </Card>
                                </Grid>
                              );
                            })}
                          </Grid>
                        )}
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {activeTab === 1 && (
                <Box>
                  <Card sx={{ ...pageCardSx, overflow: "hidden" }}>
                    <Box
                      sx={{
                        px: { xs: 1.5, sm: 2, md: 3 },
                        pt: 2.25,
                        pb: 1.5,
                        borderBottom: "1px solid rgba(47,99,173,0.08)",
                        backgroundColor: "rgba(47,99,173,0.02)",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                        <CommentIcon sx={{ color: "#2f63ad" }} />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
                          Comments
                        </Typography>
                      </Box>

                      <Typography variant="body2" sx={{ color: "#6b778c" }}>
                        Review violation comment history and attachments.
                      </Typography>
                    </Box>

                    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2.5 }}>
                        <Button
                          variant="contained"
                          startIcon={<AddCommentIcon />}
                          sx={primaryButtonSx}
                          onClick={() => setIsCommentModalOpen(true)}
                        >
                          Add Comment
                        </Button>
                      </Box>

                      {comments.length > 0 ? (
                        <Stack spacing={2}>
                          {comments.map((c) => (
                            <Card
                              key={c.comment_id}
                              sx={{
                                borderRadius: "16px",
                                border: "1px solid rgba(47,99,173,0.08)",
                                boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
                                backgroundColor: "#fff",
                                overflow: "hidden",
                              }}
                            >
                              <Box
                                sx={{
                                  px: 2,
                                  py: 1.5,
                                  borderBottom: "1px solid rgba(47,99,173,0.08)",
                                  backgroundColor: "rgba(47,99,173,0.05)",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  gap: 1.5,
                                  flexWrap: "wrap",
                                }}
                              >
                                <Chip
                                  label="External Comment"
                                  size="small"
                                  sx={{
                                    fontWeight: 700,
                                    borderRadius: "999px",
                                    color: "#1d4ed8",
                                    backgroundColor: "rgba(59,130,246,0.12)",
                                  }}
                                />
                              </Box>

                              <Box sx={{ p: 2 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "#223142",
                                    whiteSpace: "pre-wrap",
                                    mb: c.upload_urls ? 1.25 : 0,
                                  }}
                                >
                                  {c.comment_text}
                                </Typography>

                                {c.upload_urls && (
                                  <Box sx={{ mt: 1.25 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 700, color: "#223142", mb: 1 }}
                                    >
                                      Attachment
                                    </Typography>

                                    <Card
                                      sx={{
                                        ...softInsetSx,
                                        p: 2,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        gap: 1,
                                        textAlign: "center",
                                        maxWidth: 260,
                                        backgroundColor: "#fff",
                                      }}
                                    >
                                      <Box
                                        component="img"
                                        src={`${API_BASE_URL}/violationsimages/${encodeURIComponent(
                                          c.upload_urls
                                        )}`}
                                        alt="Comment Attachment"
                                        sx={{
                                          maxWidth: "100%",
                                          maxHeight: 120,
                                          borderRadius: "8px",
                                          cursor: "pointer",
                                          objectFit: "cover",
                                        }}
                                        onClick={() => {
                                          setCommentImageUrl(
                                            `${API_BASE_URL}/violationsimages/${encodeURIComponent(
                                              c.upload_urls
                                            )}`
                                          );
                                          setOpenCommentImageModal(true);
                                        }}
                                      />

                                      <Typography
                                        variant="body2"
                                        sx={{ mt: 0.5, wordBreak: "break-word" }}
                                      >
                                        Attachment Preview
                                      </Typography>

                                      <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<VisibilityIcon />}
                                        sx={secondaryButtonSx}
                                        onClick={() => {
                                          setCommentImageUrl(
                                            `${API_BASE_URL}/violationsimages/${encodeURIComponent(
                                              c.upload_urls
                                            )}`
                                          );
                                          setOpenCommentImageModal(true);
                                        }}
                                      >
                                        Preview
                                      </Button>
                                    </Card>
                                  </Box>
                                )}

                                <Divider
                                  sx={{ my: 1.5, borderColor: "rgba(47,99,173,0.08)" }}
                                />

                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#6b778c",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <AccessTimeIcon sx={{ fontSize: 14 }} />
                                  Added by {c.user_name} {c.user_lastname || ""} on{" "}
                                  {new Date(c.sent_at).toLocaleString()}
                                </Typography>
                              </Box>
                            </Card>
                          ))}
                        </Stack>
                      ) : (
                        <Card sx={{ ...pageCardSx, p: 3, textAlign: "center" }}>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: "#223142" }}>
                            No comments yet
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.75 }}>
                            Add the first comment for this violation.
                          </Typography>

                          <Box sx={{ mt: 2 }}>
                            <Button
                              variant="contained"
                              startIcon={<AddCommentIcon />}
                              sx={primaryButtonSx}
                              onClick={() => setIsCommentModalOpen(true)}
                            >
                              Add Comment
                            </Button>
                          </Box>
                        </Card>
                      )}
                    </Box>
                  </Card>
                </Box>
              )}

              {activeTab === 2 && (
                <Card sx={{ ...softCardSx, overflow: "hidden" }}>
                  <Box
                    sx={{
                      px: 2.5,
                      pt: 2.25,
                      pb: 1.5,
                      borderBottom: "1px solid rgba(47,99,173,0.08)",
                      backgroundColor: "rgba(47,99,173,0.02)",
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
                      Visits
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.4 }}>
                      Scheduled and completed visits for this violation.
                    </Typography>
                  </Box>

                  <Box sx={{ p: 2.5 }}>
                    <Visits violation_id={id} currentUserId={currentUserId} />
                  </Box>
                </Card>
              )}
            </Box>
          </Card>
        </Grid>
      </Grid>

      <Dialog
        open={openImageModal}
        onClose={() => setOpenImageModal(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogContent
          sx={{
            position: "relative",
            backgroundColor: "#111",
            p: 2,
            textAlign: "center",
          }}
        >
          <IconButton
            onClick={() => setOpenImageModal(false)}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              zIndex: 1,
              bgcolor: "rgba(0,0,0,0.45)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
            }}
          >
            <CloseIcon sx={{ color: "white" }} />
          </IconButton>

          <Box
            component="img"
            src={imageURL}
            alt="Violation Preview"
            sx={{
              maxWidth: "100%",
              maxHeight: "85vh",
              objectFit: "contain",
              borderRadius: "10px",
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={openCommentImageModal}
        onClose={() => setOpenCommentImageModal(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogContent
          sx={{
            position: "relative",
            backgroundColor: "#111",
            p: 2,
            textAlign: "center",
          }}
        >
          <IconButton
            onClick={() => setOpenCommentImageModal(false)}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              zIndex: 1,
              bgcolor: "rgba(0,0,0,0.45)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
            }}
          >
            <CloseIcon sx={{ color: "white" }} />
          </IconButton>

          <Box
            component="img"
            src={commentImageUrl}
            alt="Comment Preview"
            sx={{
              maxWidth: "100%",
              maxHeight: "85vh",
              objectFit: "contain",
              borderRadius: "10px",
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={previewModal.open}
        onClose={closePreview}
        maxWidth={previewModal.isPdf ? "xl" : "lg"}
        fullWidth
        PaperProps={{
          sx: {
            ...dialogPaperSx,
            ...(previewModal.isPdf && { height: "92vh" }),
          },
        }}
      >
        <DialogContent
          sx={{
            position: "relative",
            backgroundColor: previewModal.isPdf ? "#fff" : "#111",
            p: previewModal.isPdf ? 0 : 2,
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          <IconButton
            onClick={closePreview}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              zIndex: 10,
              bgcolor: previewModal.isPdf ? "rgba(0,0,0,0.12)" : "rgba(0,0,0,0.45)",
              "&:hover": { bgcolor: previewModal.isPdf ? "rgba(0,0,0,0.25)" : "rgba(0,0,0,0.7)" },
            }}
          >
            <CloseIcon sx={{ color: previewModal.isPdf ? "#223142" : "white" }} />
          </IconButton>

          {previewModal.isPdf ? (
            previewModal.loading ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, minHeight: "80vh" }}>
                <CircularProgress />
              </Box>
            ) : (
              <iframe
                src={previewModal.blobUrl || previewModal.src}
                title="PDF Preview"
                width="100%"
                style={{ flex: 1, border: "none", borderRadius: "18px", minHeight: "80vh" }}
              />
            )
          ) : (
            <Box
              component="img"
              src={previewModal.src}
              alt="preview"
              sx={{
                maxWidth: "100%",
                maxHeight: "85vh",
                objectFit: "contain",
                borderRadius: "10px",
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Modal open={isCommentModalOpen} onClose={() => setIsCommentModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "92%", sm: 520 },
            maxHeight: "90vh",
            overflowY: "auto",
            backgroundColor: "rgba(255,255,255,0.98)",
            padding: 0,
            borderRadius: "18px",
            boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
            border: "1px solid rgba(47,99,173,0.08)",
          }}
        >
          <Box
            sx={{
              px: 3,
              pt: 2.5,
              pb: 1.5,
              borderBottom: "1px solid rgba(47,99,173,0.08)",
              backgroundColor: "rgba(47,99,173,0.02)",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
              Add Comment
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.5 }}>
              Add a comment and optionally attach an image.
            </Typography>
          </Box>

          <Box sx={{ p: 3 }}>
            <TextField
              fullWidth
              label="Comment"
              multiline
              rows={3}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              sx={{ ...textFieldSx, mb: 2 }}
            />

            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontWeight: 600, color: "#223142" }}
              >
                Attach Image
              </Typography>

              <Box
                {...getRootProps()}
                sx={{
                  ...softInsetSx,
                  p: 2.5,
                  textAlign: "center",
                  cursor: "pointer",
                  borderStyle: "dashed",
                  borderWidth: "2px",
                }}
              >
                <input {...getInputProps()} />
                <CloudUploadIcon sx={{ fontSize: 40, color: "#1976d2", mb: 1 }} />
                <Typography variant="body2" sx={{ color: "#6b778c" }}>
                  Drag & drop an image, or click to select
                </Typography>
              </Box>
            </Box>

            {filePreview && (
              <Box sx={{ textAlign: "center", mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, color: "#223142" }}>
                  Preview
                </Typography>
                <img
                  src={filePreview}
                  alt="Preview"
                  style={{ maxWidth: "100%", maxHeight: 150, borderRadius: 8 }}
                />
              </Box>
            )}

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.25 }}>
              <Button
                variant="outlined"
                onClick={() => setIsCommentModalOpen(false)}
                sx={secondaryButtonSx}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                onClick={handleAddComment}
                disabled={isSubmitting}
                sx={primaryButtonSx}
              >
                {isSubmitting ? "Saving..." : "Save Comment"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={showConfirm}
        autoHideDuration={3000}
        onClose={() => setShowConfirm(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" variant="filled" sx={{ borderRadius: 3 }}>
          Comment added successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MemberViolationDetails;